/**
 * Who is signed in. Every working screen sits behind `RequireSession`: without a session
 * the app goes to the sign-in screen and comes back after it. The session's CSRF token is
 * handed to the client as soon as it is read, and signing in or out empties every cached
 * answer, so nothing one account saw is ever shown to the next on the same browser.
 */
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { ApiError, api, setCsrfToken, signInChanged } from "@/api/client";
import type { Account, SessionInfo, TwoStepChallenge } from "@/api/models";

export const SESSION_KEY = ["session"] as const;

async function readSession(): Promise<SessionInfo> {
  const session = await api.session();
  setCsrfToken(session.csrf_token);
  return session;
}

export const useSession = () =>
  useQuery({ queryKey: SESSION_KEY, queryFn: readSession, retry: false, staleTime: Infinity, refetchOnWindowFocus: false });

const NOBODY: Account = { email: "", role: "agent", counties: [], two_step: false, two_step_required: false };

/** A sign-in that asks next for the account's authenticator code. */
export function isChallenge(answer: SessionInfo | TwoStepChallenge): answer is TwoStepChallenge {
  return "ticket" in answer;
}

/** The signed-in account, inside `RequireSession`; for the moment a sign-out leaves the
 * screen, nobody, with no county and no admin rights. */
export function useAccount(): Account {
  return useSession().data?.account ?? NOBODY;
}

export function isSignedOut(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/** Where to go after signing in: a path of this app, never another site. */
export function safeNext(value: string | null): string {
  return value && value.startsWith("/") && !value.startsWith("//") && !value.startsWith("/sign-in") ? value : "/today";
}

export function signInHref(location: { pathname: string; search: string }): string {
  return `/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`;
}

/** Forget every answer of the account that was signed in, then hold the new session. */
export function startSession(client: QueryClient, session: SessionInfo | null) {
  signInChanged();
  client.clear();
  if (session) {
    setCsrfToken(session.csrf_token);
    client.setQueryData(SESSION_KEY, session);
  } else {
    setCsrfToken("");
  }
}

export function useSignOut() {
  const client = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: api.signOut,
    // Leave the screens first, then forget their answers, so no screen renders without its session.
    onSettled: async () => {
      await navigate("/sign-in", { replace: true });
      startSession(client, null);
    },
  });
}
